<script setup>
import { onMounted, ref } from "vue";
import Modal from "@/components/Modal";
import InputField from "@/components/forms/InputField";
import { validateUKPostcode } from "@/components/methods/index.js";

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    venueId: null,
    venue: {
        venueName: "",
        address1: "",
        area: "",
        city: "",
        postcode: "",
        country: "",
    },
    info: {
        recurrence: "",
        title: "",
        time: "",
    },
    listingsAmount: {
        type: Number,
        default: 0,
    },
    showRemoveButton: {
        type: Boolean,
        default: false,
    },
    showSpinner: {
        type: Boolean,
        default: false,
    },
    showRemoveSpinner: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["close", "edit", "delete"]);

const venueData = ref({
    venueName: "",
    address1: "",
    area: "",
    city: "",
    postcode: "",
    country: "",
});

const venueErrors = ref({});

const showConfirmDelete = ref(false);

onMounted(() => {
    venueData.value = props.venue;
});

function showDeleteVenue() {
    showConfirmDelete.value = true;
}

function validateAndEmitIfOk() { // See AutocompleteWithMap.vue
    venueErrors.value.venueName = (venueData.value.venueName === "")
        ? "Enter a venue name"
        : "";
    venueErrors.value.address1 = (venueData.value.address1 === "")
        ? "Enter an address"
        : "";
    venueErrors.value.area = (venueData.value.area === "" || venueData.value.area === "N/A")
        ? "Enter an area"
        : "";
    venueErrors.value.city = (venueData.value.city === "")
        ? "Enter a town/city"
        : "";
    venueErrors.value.postcode = (venueData.value.postcode === "")
        ? "Enter a postcode"
        : "";
    venueErrors.value.country = (venueData.value.country === "")
        ? "Enter a country"
        : "";

    if (venueErrors.value.postcode === "") { // if its blank skip this (as already handled)
        const validUkPostcodeFormat = validateUKPostcode(venueData.value.postcode);
        if (!validUkPostcodeFormat) {
            venueErrors.value.postcode = "This postcode is not valid";
        }
    }

    if (Object.values(venueErrors.value).filter((v) => v !== "").length === 0) { // passed validation
        emit("edit", venueData.value);
    }
}

</script>

<template>
    <Modal
        :show-modal="props.show && !showConfirmDelete"
        :new-header="true"
        :header-options="{
            recurrence: props.info.recurrence,
            title: props.info.title,
            time: props.info.time,
        }"
        :default-footer="!showRemoveButton"
        submit-text="Save"
        :close-text="!showRemoveButton ? 'Close' : 'Delete venue'"
        :show-spinner="showSpinner"
        @close-modal="emit('close')"
        @submit-modal="validateAndEmitIfOk();"
        @submit-delete="showDeleteVenue"
    >
        <h2 class="my-3">Edit venue</h2>

        <div class="mt-4 manual-address-container">
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Venue name"
                    :required="true"
                    v-model="venueData.venueName"
                    :value="props.venue.venueName"
                    :error="venueErrors.venueName"
                />
            </div>
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Address"
                    :required="true"
                    :maxlength="255"
                    v-model="venueData.address1"
                    :value="props.venue.address1"
                    :error="venueErrors.address1"
                />
            </div>
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Area"
                    subLabelText="
                        Please enter an area name.
                        This will help your customers
                        easily differentiate between multiple venues of your business.
                    "
                    :required="true"
                    v-model="venueData.area"
                    :value="props.venue.area"
                    :error="venueErrors.area"
                />
            </div>
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Town / City"
                    :required="true"
                    v-model="venueData.city"
                    :value="props.venue.city"
                    :error="venueErrors.city"
                />
            </div>
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Post code"
                    :required="true"
                    v-model="venueData.postcode"
                    :value="props.venue.postcode"
                    :error="venueErrors.postcode"
                />
            </div>
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Country"
                    :disabled="true"
                    :required="true"
                    v-model="venueData.country"
                    :value="props.venue.country"
                />
            </div>
        </div>
    </Modal>

    <Modal
        :show-modal="showConfirmDelete"
        :show-spinner="showRemoveSpinner"
        :remove-modal="true"
        close-text="Cancel"
        close-btn-class="primary"
        title="Delete venue"
        submit-text="Delete"
        submit-btn-class="delete-button"
        modal-dialog-class="md-sm"
        @close-modal="showConfirmDelete = false"
        @submit-modal="emit('delete')"
    >
        <div class="mt-3">
            Are you sure you want to delete the <span class="font-weight-bolder">
                {{ props.venue.venueName }}, {{ props.venue.area }}
            </span> venue?
            This will delete <span class="font-weight-bolder">{{ listingsAmount }}</span> listings.
        </div>
    </Modal>
</template>

<style scoped>

</style>
